import "antd/dist/antd.css";
import "../App.css";

import { Link, useLoaderData } from "react-router-dom";
import { apiFetch } from "../apiFetch";
import Section from "../components/Section";
import usePageMeta from "../usePageMeta";
import { jsonLdString } from "../jsonLd";

type TemplateCard = {
    identifier: string;
    slug: string;
    name: string;
    summary?: string;
    region?: string;
    country?: string;
    transportMethod?: string;
    durationDays?: number;
    difficulty?: string;
    season?: string;
    heroImageUrl?: string | null;
};

export const exploreLoader = async () => {
    // The prerender step runs against a local static server that has
    // no route to the real API. Any network/parse failure has to fall
    // back to an empty template list so React Router still renders the
    // page instead of routing to errorElement — otherwise puppeteer
    // waits for the header wordmark that never mounts and the build
    // fails with "Waiting for selector `.gt-wordmark` failed".
    let response;
    try {
        response = await apiFetch(`/tour-templates`);
    } catch {
        return { templates: [] as TemplateCard[] };
    }
    if (!response.ok) return { templates: [] as TemplateCard[] };
    try {
        const result = await response.json();
        return { templates: (result.templates || []) as TemplateCard[] };
    } catch {
        return { templates: [] as TemplateCard[] };
    }
};

function Explore() {
    const { templates } = useLoaderData() as { templates: TemplateCard[] };
    usePageMeta({
        title: "Explore tours — ready-made motorbike trips you can plan in a tap",
        description:
            "Browse hand-curated multi-day motorbike tours — the North Coast 500, Stelvio Pass and more. Clone one to set dates, invite mates and refine the route.",
        url: "https://www.planamoto.com/explore",
    });

    // CollectionPage + ItemList so the hub page carries structured data
    // like the rest of the site. The ItemList is only populated when the
    // loader had API access (live traffic / Google's JS rendering); the
    // prerendered snapshot ships the CollectionPage shell without items.
    const collectionLd: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Explore tours — ready-made motorbike trips you can plan in a tap",
        url: "https://www.planamoto.com/explore",
        description:
            "Hand-curated multi-day motorbike and cycle touring routes you can clone and customise on Planamoto.",
    };
    if (templates.length > 0) {
        collectionLd.mainEntity = {
            "@type": "ItemList",
            itemListElement: templates.map((t, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: t.name,
                url: `https://www.planamoto.com/explore/${t.slug}`,
            })),
        };
    }
    const breadcrumbLd = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.planamoto.com" },
            { "@type": "ListItem", position: 2, name: "Explore tours", item: "https://www.planamoto.com/explore" },
        ],
    };

    return (
        <div className="gt-explore">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: jsonLdString(collectionLd) }}
            />
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: jsonLdString(breadcrumbLd) }}
            />
            <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", width: "100%" }}>
                <Section
                    eyebrow="Ready-made tours"
                    heading="Pick a route, clone it, ride it"
                    intro="Each template gives you a day-by-day plan with overnight areas and recommended stops. Clone it into your own tours, set the dates, invite your mates."
                    align="center"
                >
                    {templates.length === 0 ? (
                        <div
                            className="gt-card gt-body"
                            style={{
                                padding: 32,
                                textAlign: "center",
                                color: "var(--gt-text-muted)",
                            }}
                        >
                            No tour templates published yet — check back soon.
                        </div>
                    ) : (
                        <div className="gt-grid--cards gt-explore__grid">
                            {templates.map((t) => (
                                <Link
                                    key={t.identifier}
                                    to={`/explore/${t.slug}`}
                                    className="gt-card gt-card--clickable gt-appear gt-template-card"
                                >
                                    {t.heroImageUrl ? (
                                        <img
                                            src={t.heroImageUrl}
                                            alt=""
                                            loading="lazy"
                                            className="gt-template-card__image"
                                        />
                                    ) : (
                                        <div className="gt-template-card__image gt-template-card__image--placeholder" />
                                    )}
                                    <div className="gt-template-card__body">
                                        <div className="gt-eyebrow gt-template-card__meta">
                                            {[
                                                t.region,
                                                t.durationDays ? `${t.durationDays} days` : null,
                                                t.difficulty,
                                            ]
                                                .filter(Boolean)
                                                .join(" · ")}
                                        </div>
                                        <h3 className="gt-h4 gt-template-card__title">
                                            {t.name}
                                        </h3>
                                        {t.summary && (
                                            <p className="gt-template-card__summary">
                                                {t.summary}
                                            </p>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </Section>
            </div>
        </div>
    );
}

export default Explore;
